import { demographics } from "../../patient-stats/demographics/model/demographics";
import { IDemographicsRecord } from "../../../entities/patient-stats/demographics/demographics.selectors";
import { ROI_REQUEST_SELECTOR_SECTIONS } from "../../roi-request-create/model/roi-request-create.dataset";
import { IDemoPersistedRoiRequest } from "../../roi-request-create/model/roi-request-create.storage";
import {
  RoiSelectorColumn,
  RoiSelectorRow,
} from "../../roi-request-create/model/roi-request-create.types";

export interface IRoiRequestViewDetailsRow {
  label: string;
  value: string;
}

export interface IRoiRequestViewDetailsGroup {
  title?: string;
  rows: IRoiRequestViewDetailsRow[];
}

interface IRoiRequestViewSectionBase {
  id: string;
  label: string;
}

export interface IRoiRequestViewDetailsSection
  extends IRoiRequestViewSectionBase {
  type: "details";
  groups: IRoiRequestViewDetailsGroup[];
}

export interface IRoiRequestViewTableSection
  extends IRoiRequestViewSectionBase {
  type: "table";
  columns: RoiSelectorColumn[];
  rows: RoiSelectorRow[];
}

export type IRoiRequestViewSection =
  | IRoiRequestViewDetailsSection
  | IRoiRequestViewTableSection;

const getDemoDemographicsRecord = (patientId: string) =>
  (demographics as IDemographicsRecord[]).find(
    (record) => record.patientId === patientId,
  );

const getDemographicsDetailsGroups = (
  demographicsRecord: IDemographicsRecord,
): IRoiRequestViewDetailsGroup[] => [
  {
    title: "Patient Overview",
    rows: [
      {
        label: "Full Name",
        value: demographicsRecord.fullName,
      },
      {
        label: "Date of Birth",
        value: demographicsRecord.dateOfBirth,
      },
      {
        label: "Sex",
        value: demographicsRecord.sex,
      },
      {
        label: "Race",
        value: demographicsRecord.race,
      },
      {
        label: "Ethnicity Code",
        value: demographicsRecord.ethnicityCode,
      },
      {
        label: "Language",
        value: demographicsRecord.language,
      },
      {
        label: "Marital Status",
        value: demographicsRecord.maritalStatus,
      },
      {
        label: "Patient Number",
        value: demographicsRecord.patientNumber,
      },
      {
        label: "Social Security Number",
        value: demographicsRecord.socialSecurityNumber,
      },
      {
        label: "Blood Type",
        value: demographicsRecord.bloodType,
      },
    ],
  },
  {
    title: "Care Team & Providers",
    rows: [
      {
        label: "Referring Provider",
        value: demographicsRecord.referringProvider,
      },
      {
        label: "Referring Provider Phone",
        value: demographicsRecord.referringProviderPhone,
      },
      {
        label: "PCP Provider",
        value: demographicsRecord.pcpProvider,
      },
      {
        label: "PCP Provider Phone",
        value: demographicsRecord.pcpProviderPhone,
      },
      {
        label: "Usual Caregiver",
        value: demographicsRecord.usualCaregiver,
      },
      {
        label: "Usual Caregiver Phone",
        value: demographicsRecord.usualCaregiverPhone,
      },
      {
        label: "Privacy Status",
        value: demographicsRecord.privacyStatus,
      },
    ],
  },
  {
    title: "Home Address & Contacts",
    rows: [
      { label: "Address", value: demographicsRecord.homeAddress },
      {
        label: "City / State / Zip",
        value: demographicsRecord.homeCityStateZip,
      },
      { label: "County", value: demographicsRecord.homeCounty },
      {
        label: "Cross Street",
        value: demographicsRecord.homeCrossStreet,
      },
      { label: "Phone", value: demographicsRecord.homePhone },
      { label: "Email", value: demographicsRecord.homeEmail },
    ],
  },
  {
    title: "Office Address & Contacts",
    rows: [
      { label: "Address", value: demographicsRecord.officeAddress },
      {
        label: "City / State / Zip",
        value: demographicsRecord.officeCityStateZip,
      },
      { label: "County", value: demographicsRecord.officeCounty },
      {
        label: "Cross Street",
        value: demographicsRecord.officeCrossStreet,
      },
      { label: "Phone", value: demographicsRecord.officePhone },
      { label: "Email", value: demographicsRecord.officeEmail },
    ],
  },
  {
    title: "Employment",
    rows: [
      { label: "Employer", value: demographicsRecord.employer },
      {
        label: "Employer Status",
        value: demographicsRecord.employerStatus,
      },
      { label: "Occupation", value: demographicsRecord.occupation },
      { label: "Industry", value: demographicsRecord.industry },
    ],
  },
  {
    title: "Guarantor",
    rows: [
      {
        label: "Full Name",
        value: demographicsRecord.guarantorFullName,
      },
      {
        label: "Relationship",
        value: demographicsRecord.guarantorRelationship,
      },
      {
        label: "Sex",
        value: demographicsRecord.guarantorSex,
      },
      {
        label: "Date of Birth",
        value: demographicsRecord.guarantorDateOfBirth,
      },
      {
        label: "Social Security Number",
        value: demographicsRecord.guarantorSSN,
      },
    ],
  },
  {
    title: "Local Pharmacy",
    rows: [
      {
        label: "Pharmacy Name",
        value: demographicsRecord.pharmacyName,
      },
      { label: "Address", value: demographicsRecord.pharmacyAddress },
      {
        label: "City / State / Zip",
        value: demographicsRecord.pharmacyCityStateZip,
      },
      { label: "Phone", value: demographicsRecord.pharmacyPhone },
      { label: "Fax", value: demographicsRecord.pharmacyFax },
      { label: "Email", value: demographicsRecord.pharmacyEmail },
    ],
  },
  {
    title: "Administrative Info",
    rows: [
      { label: "Status", value: demographicsRecord.status },
      { label: "Patient Type", value: demographicsRecord.patientType },
      { label: "Medic Alert", value: demographicsRecord.medicAlert },
      { label: "Organ Donor", value: demographicsRecord.organDonor },
      { label: "VIP Indicator", value: demographicsRecord.vipIndicator },
      { label: "Chart ID", value: demographicsRecord.chartId },
      { label: "Account ID", value: demographicsRecord.accountId },
      {
        label: "Release of Info",
        value: demographicsRecord.releaseOfInfo,
      },
      {
        label: "HCFA Signature",
        value: demographicsRecord.hcfaSignature,
      },
      {
        label: "Notify Method",
        value: demographicsRecord.notifyMethod,
      },
    ],
  },
  {
    title: "Contacts",
    rows: demographicsRecord.contacts.map((contact, index) => ({
      label: `Contact ${index + 1}`,
      value: `${contact.firstName} ${contact.lastName} | ${contact.relationship} | ${contact.phone} | Emergency: ${contact.emergency}`,
    })),
  },
];

const getDefaultDemographicsSection = (
  patientId: string,
): IRoiRequestViewDetailsSection | null => {
  const demographicsRecord = getDemoDemographicsRecord(patientId);

  if (!demographicsRecord) {
    return null;
  }

  return {
    id: "demographics",
    label: "Demographics",
    type: "details",
    groups: getDemographicsDetailsGroups(demographicsRecord),
  };
};

export const getRoiRequestViewSections = ({
  patientId,
  request,
}: {
  patientId: string;
  request: IDemoPersistedRoiRequest;
}): IRoiRequestViewSection[] => {
  const demographicsSection = getDefaultDemographicsSection(patientId);

  const selectedSections = request.data
    .map((selectedSection) => {
      const sourceSection = ROI_REQUEST_SELECTOR_SECTIONS.find(
        (section) => section.section_id === selectedSection.section_id,
      );

      if (!sourceSection) {
        return null;
      }

      return {
        id: sourceSection.section_id,
        label: sourceSection.label,
        type: "table" as const,
        columns: sourceSection.table.columns,
        rows: sourceSection.table.rows.filter((row) =>
          selectedSection.selection.includes(row.data_element_id),
        ),
      };
    })
    .filter(
      (section): section is IRoiRequestViewTableSection =>
        section !== null && section.rows.length > 0,
    );

  return demographicsSection
    ? [demographicsSection, ...selectedSections]
    : selectedSections;
};
